import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ApiBudgetGauge } from './ApiBudgetGauge';
import { BotActivityPanel } from './BotActivityPanel';
import { DiagnosticsPanel } from './DiagnosticsPanel';
import { ForeignStandoffBanner } from './ForeignStandoffBanner';

const API = window.location.origin;

/**
 * ANTI-FLICKER (Multi-Pod K8s Fix — Final)
 *
 * Problem: In Kubernetes with multiple pods, only ONE pod has the bot
 * in memory. Other pods know it's running (via MongoDB) but have no
 * grid/stats/inventory data. This causes content to flicker between
 * "full dashboard" and "empty dashboard" on each poll.
 *
 * Solution:
 * 1. Time-based debounce for RUNNING→STOPPED (60s grace period)
 * 2. Only update displayed data when response has REAL bot data
 *    (grid levels, stats). Ignore empty "running" responses from
 *    pods that only know the bot is running via MongoDB.
 */
const GRACE_PERIOD_MS = 60000;
const STABLE_DATA_TOLERANCE_MS = 60000;

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

const mergeDefinedDeep = (base, patch) => {
  if (patch === undefined || patch === null) return base;
  if (Array.isArray(patch)) return patch;
  if (!isObj(patch)) return patch;

  const out = isObj(base) ? { ...base } : {};
  Object.keys(patch).forEach((key) => {
    out[key] = mergeDefinedDeep(out[key], patch[key]);
  });
  return out;
};

const countActiveGridLevels = (payload) => {
  const buy = payload?.grid?.buy_levels?.length || 0;
  const sell = payload?.grid?.sell_levels?.length || 0;
  return buy + sell;
};

const statsQualityScore = (stats) => {
  if (!stats || typeof stats !== 'object') return 0;
  let score = 0;
  if (stats.running === true) score += 1;
  if ((stats.total_round_trips || 0) > 0) score += 1;
  if ((stats.orders_placed || 0) > 0) score += 1;
  if ((stats.winning_trips || 0) > 0) score += 1;
  if ((stats.win_rate || 0) > 0) score += 1;
  return score;
};

const mergeRunningPayload = (previous, incoming, now, lastStableAtMs) => {
  if (!previous) return incoming;
  const withinTolerance = (now - lastStableAtMs) <= STABLE_DATA_TOLERANCE_MS;
  const merged = mergeDefinedDeep(previous, incoming);
  const exchangeQueryOk = incoming?.exchange_position_query_ok;
  const exchangeFlatConfirmed = exchangeQueryOk === true && !incoming?.exchange_position;

  const incomingGridCount = countActiveGridLevels(incoming);
  const previousGridCount = countActiveGridLevels(previous);
  // Intentional-empty detection (24 May 2026):
  // If the engine has legitimately cleared the grid (user paused the
  // current regime, OR is_flat after a basket close, OR warmup gate),
  // the new payload carries empty buy/sell arrays — we must NOT carry
  // forward the previous (cancelled) levels or the dashboard shows
  // phantom rungs until the user refreshes the page.
  const incomingRegime = incoming?.hydra?.regime?.regime;
  const allowedRegimes = incoming?.allowed_trade_regimes;
  const regimeIsPaused = (
    Array.isArray(allowedRegimes)
    && incomingRegime
    && !allowedRegimes.includes(incomingRegime)
  );
  const warmingUp = incoming?.regime_warmup?.warming_up === true;
  const intentionallyEmpty = regimeIsPaused || warmingUp;
  if (
    withinTolerance
    && incomingGridCount === 0
    && previousGridCount > 0
    && incoming?.running === true
    && !intentionallyEmpty
  ) {
    merged.grid = previous.grid;
  }

  // Never keep a stale exchange position when the latest payload confirms flat.
  if (exchangeFlatConfirmed) {
    merged.exchange_position = null;
  }

  const incomingInvCount = incoming?.inventory?.count ?? 0;
  const previousInvCount = previous?.inventory?.count ?? 0;
  const allowInventoryCarry = incoming?.data_source === 'db_state_fallback' && exchangeFlatConfirmed !== true;
  if (withinTolerance && allowInventoryCarry && incomingInvCount === 0 && previousInvCount > 0 && incoming?.running === true) {
    merged.inventory = previous.inventory;
  }

  if (exchangeFlatConfirmed) {
    merged.inventory = incoming?.inventory || {
      inventory: [],
      count: 0,
      long_count: 0,
      short_count: 0,
      total_unrealized: 0,
    };
  }

  const incomingStatsScore = statsQualityScore(incoming?.stats);
  const previousStatsScore = statsQualityScore(previous?.stats);
  if (withinTolerance && incomingStatsScore < previousStatsScore) {
    merged.stats = previous.stats;
  }

  return merged;
};

const cbColors = { green: '#22c55e', yellow: '#eab308', red: '#ef4444' };
const regimeLabels = { ranging: 'RANGING', mild_trend: 'MILD TREND', strong_trend: 'STRONG TREND' };
const regimeColors = { ranging: '#22c55e', mild_trend: '#eab308', strong_trend: '#ef4444' };

const HydraStatus = ({ hydra }) => {
  if (!hydra) return null;
  const cb = hydra.circuit_breaker || {};
  const regime = hydra.regime || {};
  const vol = hydra.volatility || {};
  const fund = hydra.funding || {};
  const heal = hydra.healer || {};
  const rebal = hydra.rebalance || {};

  const items = [
    {
      label: 'Circuit Breaker',
      value: (cb.state || 'green').toUpperCase(),
      color: cbColors[cb.state] || '#22c55e',
      sub: cb.hourly_pnl != null ? `1hr P&L: $${cb.hourly_pnl?.toFixed(4)}` : null,
    },
    {
      label: 'Market Regime',
      value: regimeLabels[regime.regime] || 'RANGING',
      color: regimeColors[regime.regime] || '#22c55e',
      sub: `ADX: ${regime.adx?.toFixed(1) || '—'} / BBW: ${regime.bb_width_pct?.toFixed(2) || '—'}%`,
    },
    {
      label: 'Volatility',
      value: vol.current_spacing_pct != null ? `${vol.current_spacing_pct?.toFixed(3)}%` : 'Waiting',
      color: '#60a5fa',
      sub: `ATR: $${vol.atr?.toFixed(2) || '—'}`,
    },
    {
      label: 'Funding Bias',
      value: fund.bias ? `${fund.bias.buy_levels}B/${fund.bias.sell_levels}S` : '—',
      color: '#a78bfa',
      sub: `Rate: ${fund.funding_rate_pct?.toFixed(4) || '0'}%`,
    },
    {
      label: 'Inv. Healer',
      value: `${heal.total_healed || 0} healed`,
      color: '#2dd4bf',
    },
    {
      label: 'Smart Rebalance',
      value: rebal.outside_timer > 0 ? `${rebal.outside_timer}s / ${rebal.lazy_seconds}s` : 'Idle',
      color: rebal.outside_timer > 0 ? '#f59e0b' : '#6b7280',
    },
  ];

  return (
    <div data-testid="hydra-status" style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#a78bfa', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        HYDRA
        <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 400 }}>Self-Healing System</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 6 }}>
        {items.map((item, i) => (
          <div key={`hydra-${item.label}`} data-testid={`hydra-${item.label.toLowerCase().replace(/[.\s]/g, '-')}`} style={{
            background: '#0f172a', borderRadius: 6, padding: '6px 10px',
            borderLeft: `3px solid ${item.color}`,
          }}>
            <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 2 }}>{item.label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: item.color }}>{item.value}</div>
            {item.sub && <div style={{ fontSize: 10, color: '#4b5563', marginTop: 1 }}>{item.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const GridBotDashboard = ({ onStatusChange }) => {
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null); // ms timestamp of last successful fetch
  const [fetchError, setFetchError] = useState(false);  // true after a failed fetch, false on first success
  const [, setTick] = useState(0);  // 1s tick for "X sec ago" rendering
  const [editingParam, setEditingParam] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ stats: true, grid: true, params: false });
  const intervalRef = useRef(null);
  const lastNotifiedRunning = useRef(null);
  const lastRunningAtRef = useRef(0);
  const hasCachedDataRef = useRef(false);
  const latestDataRef = useRef(null);
  const lastStableDataAtRef = useRef(0);
  const lastOwnerAtRef = useRef(0);

  const fetchDashboard = useCallback(async () => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000); // 4s timeout — prevents stacking
      // FIX 165: Include auth token so backend routes to the correct user's bot
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/grid-bot/dashboard`, { signal: ctrl.signal, headers });
      clearTimeout(t);
      if (!res.ok) {
        setFetchError(true);
        return;
      }

      const json = await res.json();
      const isRunning = json.running === true;
      const now = Date.now();
      setLastUpdated(now);
      setFetchError(false);
      const dataSource = json?.data_source;

      if (isRunning) {
        lastRunningAtRef.current = now;

        // ── Multi-pod flicker guard (28 Jun 2026) ──────────────────────────
        // Production runs N pods behind a load balancer. The OWNING pod returns
        // fresh in-memory data (data_source 'local_engine'); non-owning pods
        // return a LAGGING persisted snapshot ('db_state_replica'). Polls
        // alternate between pods, so the dashboard flip-flopped between two
        // different regimes/grids every 5s. Once we're showing the owner's live
        // data, IGNORE replica snapshots from the same session — the owner is
        // authoritative. Failover escape: if the owner goes silent for >30s
        // (pod died / handed off), accept replica data again so the UI isn't
        // frozen. New session (start_time changes on restart) is always accepted.
        if (dataSource === 'local_engine') lastOwnerAtRef.current = now;
        const incStart = Number(json?.stats?.start_time ?? 0);
        const curStart = Number(latestDataRef.current?.stats?.start_time ?? 0);
        const sameSession = incStart > 0 && curStart > 0 && Math.abs(incStart - curStart) < 2;
        const incomingIsReplica = dataSource !== 'local_engine';
        const showingOwner = latestDataRef.current?.data_source === 'local_engine';
        const ownerRecent = (now - lastOwnerAtRef.current) < 30000;
        if (incomingIsReplica && showingOwner && sameSession && ownerRecent) {
          // Keep the authoritative owner data on screen; just refresh status.
          if (onStatusChange) {
            onStatusChange(true, latestDataRef.current.state, latestDataRef.current);
          }
          return;
        }

        // Does this response have REAL bot data (from the pod running the bot)?
        // Or is it an empty "running" response from another pod (MongoDB flag only)?
        const hasRealData = dataSource === 'local_engine' && (
          (json.grid?.buy_levels?.length > 0) ||
          (json.grid?.sell_levels?.length > 0) ||
          (json.stats?.running === true)
        );

        if (hasRealData) {
          lastStableDataAtRef.current = now;
          // Full data from the actual bot pod — update display and mark cached
          hasCachedDataRef.current = true;
          const merged = mergeRunningPayload(latestDataRef.current, json, now, lastStableDataAtRef.current);
          setData(merged);
          latestDataRef.current = merged;
        } else if (!hasCachedDataRef.current) {
          // First time seeing running (no cached data yet) — show what we have
          const merged = mergeRunningPayload(latestDataRef.current, json, now, lastStableDataAtRef.current);
          setData(merged);
          latestDataRef.current = merged;
        } else {
          const merged = mergeRunningPayload(latestDataRef.current, json, now, lastStableDataAtRef.current);
          if (merged) {
            setData(merged);
            latestDataRef.current = merged;
          }
        }
        // else: empty running response from another pod — keep showing cached full data

        if (onStatusChange) {
          const payloadForCallback = hasRealData
            ? (latestDataRef.current || json)
            : (latestDataRef.current || json);
          onStatusChange(true, payloadForCallback.state, payloadForCallback);
        }

        if (onStatusChange && lastNotifiedRunning.current !== true) {
          lastNotifiedRunning.current = true;
          onStatusChange(true, (latestDataRef.current || json).state, (latestDataRef.current || json));
        }
      } else {
        const elapsed = now - lastRunningAtRef.current;
        // Bypass grace period ONLY for FRESH manual stops (within 30s)
        // that happened AFTER we last observed the bot running. Multi-pod
        // K8s caveat: dashboard polls alternate between pods, and a
        // non-owning pod that loses its DB read can fall into the
        // running=false fallback and surface a stale last_stop_reason
        // from a previous session. Without the `at > lastRunningAtRef`
        // guard, that stale "manual" reason would defeat the grace
        // period and the UI would flicker stopped/running/stopped.
        const stopReason = json.last_stop_reason;
        const ageS = stopReason?.at ? (Date.now() / 1000 - stopReason.at) : 1e9;
        const stopAfterRunningSeen = (
          stopReason?.at
            ? (stopReason.at * 1000) > lastRunningAtRef.current
            : false
        );
        const freshManualStop = (
          stopReason?.reason === 'manual'
          && ageS < 30
          && stopAfterRunningSeen
        );
        if (freshManualStop || lastRunningAtRef.current === 0 || elapsed > GRACE_PERIOD_MS) {
          hasCachedDataRef.current = false;
          lastStableDataAtRef.current = 0;
          setData(json);
          latestDataRef.current = json;
          if (onStatusChange && lastNotifiedRunning.current !== false) {
            lastNotifiedRunning.current = false;
            onStatusChange(false, json.state, json);
          }
        }
      }
    } catch (e) {
      /* keep stale data */
      setFetchError(true);
    }
  }, [onStatusChange]);

  useEffect(() => {
    fetchDashboard();
    intervalRef.current = setInterval(fetchDashboard, 5000);

    // 1Hz tick so the "Last updated Xs ago" label re-renders without
    // refetching. Cheap — just toggles a counter.
    const tickId = setInterval(() => setTick(t => t + 1), 1000);

    // When the tab regains focus, fetch IMMEDIATELY. Background tabs
    // get setInterval throttled to ~1 call/min by Chrome/Firefox, so
    // returning to the tab after several minutes shows stale data
    // until the next throttled tick. This handler closes that gap.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboard();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(tickId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, [fetchDashboard]);

  // Reset session stats — disabled while bot is running. Backend deletes
  // the persisted engine_state Mongo doc; All-Time/exchange numbers come
  // from Hyperliquid and are not affected.
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState(null);

  const handleResetStats = useCallback(async () => {
    setResetting(true);
    setResetError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/grid-bot/reset-stats`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Reset failed (HTTP ${res.status})`);
      }
      // Force-refresh dashboard to show the cleared counters
      await fetchDashboard();
      setResetConfirmOpen(false);
    } catch (e) {
      setResetError(e.message || 'Reset failed');
    } finally {
      setResetting(false);
    }
  }, [fetchDashboard]);

  const running = data?.running;
  const state = data?.state || 'stopped';
  const stats = data?.stats;
  const params = data?.params;
  const grid = data?.grid;
  const inventory = data?.inventory;
  const exchangePosition = data?.exchange_position;
  // Active coin. When the engine is running we trust the dashboard
  // payload's `coin` field (engine-side truth). When stopped, fall back
  // to params.symbol. The 'BTC' default is the very last resort — only
  // hit on the first paint before the dashboard fetch resolves.
  const coin = data?.coin || params?.symbol || params?.coin || null;
  // Per-coin price precision (mirrors backend grid.py TICK_SIZE).
  const formatCoinPrice = (px, c) => {
    if (px == null || isNaN(px)) return '—';
    const dp = {
      BTC: 0, ETH: 1, SOL: 2, BNB: 1, XRP: 4,
      HYPE: 3, DOGE: 5, ADA: 5, AVAX: 4, LINK: 4,
    }[c] ?? 2;
    return Number(px).toFixed(dp);
  };

  const activeBuyLevels = (grid?.buy_levels || []).filter(level => level?.status === 'active').length;
  const activeSellLevels = (grid?.sell_levels || []).filter(level => level?.status === 'active').length;
  const totalBuyLevels = grid?.buy_levels?.length || 0;
  const totalSellLevels = grid?.sell_levels?.length || 0;
  const longCount = inventory?.long_count || 0;
  const shortCount = inventory?.short_count || 0;
  const trulyExhausted = (
    (longCount > 0 && totalBuyLevels > 0 && activeBuyLevels === 0) ||
    (shortCount > 0 && totalSellLevels > 0 && activeSellLevels === 0)
  );
  const displayState = (state === 'grid_exhausted' && !trulyExhausted) ? 'running' : state;

  const handleParamSave = async (key) => {
    setSaving(true);
    try {
      const val = editValue.includes('.') ? parseFloat(editValue) : parseInt(editValue, 10);
      await fetch(`${API}/api/grid-bot/params`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ [key]: isNaN(val) ? editValue : val }),
      });
      setEditingParam(null);
      await fetchDashboard();
    } finally { setSaving(false); }
  };

  const stateColor = {
    running: '#22c55e', rebalancing: '#eab308', paused: '#f97316',
    stopped: '#6b7280', emergency_stop: '#ef4444', initializing: '#3b82f6',
    grid_exhausted: '#f59e0b', exiting: '#f97316',
  }[displayState] || '#6b7280';

  const formatPnl = (v) => {
    if (v == null) return '$0.00';
    const n = parseFloat(v);
    return `${n >= 0 ? '+' : ''}$${n.toFixed(2)}`;
  };

  const pnlColor = (v) => {
    if (v == null) return '#9ca3af';
    return parseFloat(v) >= 0 ? '#22c55e' : '#ef4444';
  };

  const renderGrid = () => {
    if (!grid || (!grid.buy_levels?.length && !grid.sell_levels?.length)) {
      return <span style={{ color: '#6b7280' }}>No grid active</span>;
    }
    const center = grid.center_price || 0;
    const current = grid.current_price || 0;
    const allLevels = [
      ...grid.buy_levels.map(l => ({ ...l, type: 'buy' })),
      ...grid.sell_levels.map(l => ({ ...l, type: 'sell' })),
    ].sort((a, b) => b.price - a.price);

    if (!allLevels.length) return <span style={{ color: '#6b7280' }}>Grid empty</span>;

    return (
      <div data-testid="grid-visualization" style={{ marginTop: 8 }}>
        {allLevels.map((l, i) => {
          const isBuy = l.type === 'buy';
          const filled = l.status === 'filled';
          const bg = isBuy ? '#15803d' : '#b91c1c';
          const opacity = filled ? 0.5 : 0.85;
          const isCurrentPrice = current > 0 && i < allLevels.length - 1 &&
            allLevels[i].price >= current && allLevels[i + 1]?.price < current;
          return (
            <React.Fragment key={`level-${l.price}-${l.side}`}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, opacity,
              }}>
                <span style={{ width: 55, fontSize: 11, color: '#9ca3af', textAlign: 'right', flexShrink: 0 }}>
                  ${formatCoinPrice(l.price, coin)}
                </span>
                <div style={{
                  flex: 1, height: 20, borderRadius: 3, background: bg,
                  display: 'flex', alignItems: 'center', paddingLeft: 8,
                }}>
                  <span style={{ fontSize: 10, color: '#e5e7eb', fontWeight: 500 }}>
                    {isBuy ? 'BUY' : 'SELL'} {l.size?.toFixed(6)} {filled ? '(filled)' : ''}
                  </span>
                </div>
              </div>
              {isCurrentPrice && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2,
                }}>
                  <span style={{ width: 55, fontSize: 11, color: '#f59e0b', textAlign: 'right', fontWeight: 700, flexShrink: 0 }}>
                    {coin} ${formatCoinPrice(current, coin)}
                  </span>
                  <div style={{ flex: 1, height: 2, background: '#f59e0b' }} />
                  {center > 0 && (
                    <span style={{ fontSize: 10, color: '#6b7280', flexShrink: 0 }}>center ${formatCoinPrice(center, coin)}</span>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderInventory = () => {
    if (!inventory?.inventory?.length) return null;
    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 6, fontWeight: 600 }}>
          Inventory ({inventory.long_count || 0} long / {inventory.short_count || 0} short)
          <span style={{ marginLeft: 8, color: pnlColor(inventory.total_unrealized) }}>
            {formatPnl(inventory.total_unrealized)} unrealized
          </span>
        </div>
        <div style={{ display: 'grid', gap: 4 }}>
          {inventory.inventory.map((inv, i) => {
            const isLong = inv.side === 'long';
            // Backend sends { side, entry_price, tp_price, size, ... }
            // Map to the price names this row expects. For a LONG, we
            // BOUGHT at entry_price and TP is a SELL at tp_price. For a
            // SHORT, we SOLD at entry_price and TP is a BUY at tp_price.
            const entryPx = Number(inv.entry_price ?? inv.buy_price ?? inv.sell_price ?? 0);
            const targetPx = Number(inv.tp_price ?? inv.buy_target_price ?? inv.sell_target_price ?? 0);
            return (
              <div key={`inv-${inv.side}-${entryPx}-${i}`} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: '#1e293b', borderRadius: 4, padding: '4px 10px', fontSize: 12,
                borderLeft: `3px solid ${isLong ? '#15803d' : '#b91c1c'}`,
              }}>
                <span style={{ color: isLong ? '#4ade80' : '#f87171', fontWeight: 600, width: 45 }}>
                  {isLong ? 'LONG' : 'SHORT'}
                </span>
                <span style={{ color: '#e5e7eb' }}>
                  {isLong ? `Buy $${formatCoinPrice(entryPx, coin)}` : `Sell $${formatCoinPrice(entryPx, coin)}`}
                </span>
                <span style={{ color: '#9ca3af' }}>
                  Target ${formatCoinPrice(targetPx, coin)}
                </span>
                {inv.open_regime && inv.open_regime !== 'unknown' && (
                  <span style={{
                    color: inv.open_regime === 'ranging' ? '#60a5fa' : inv.open_regime === 'mild_trend' ? '#fbbf24' : '#f87171',
                    fontSize: 10, fontWeight: 500, textTransform: 'uppercase',
                    background: 'rgba(255,255,255,0.05)', padding: '1px 5px', borderRadius: 3,
                  }} data-testid={`inventory-regime-${i}`}>
                    {inv.open_regime.replace('_', ' ')}
                  </span>
                )}
                <span style={{ color: pnlColor(inv.unrealized_pnl) }}>{formatPnl(inv.unrealized_pnl)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const paramLabels = {
    grid_spacing_pct: { label: 'Grid Spacing %', fmt: v => `${(v * 100).toFixed(2)}%` },
    grid_levels: { label: 'Grid Levels', fmt: v => v },
    notional_per_level: { label: '$ per Level', fmt: v => `$${v}` },
    leverage: { label: 'Leverage', fmt: v => `${v}x` },
    counter_spacing_mult: { label: 'TP Multiplier', fmt: v => `${v}x` },
    max_inventory_levels: { label: 'Max Inventory', fmt: v => v },
    rebalance_interval: { label: 'Rebalance Interval', fmt: v => `${v}s` },
    smart_stop_multiplier: { label: 'Smart Stop x', fmt: v => `${v}x` },
    smart_stop_cooldown_seconds: { label: 'Cooldown', fmt: v => `${v}s` },
    equity_loss_cap_pct: { label: 'Equity Cap', fmt: v => `${(v * 100).toFixed(0)}%` },
    maker_fee: { label: 'Maker Fee', fmt: v => `${(v * 100).toFixed(4)}%` },
    poll_interval: { label: 'Poll Interval', fmt: v => `${v}s` },
  };

  const sectionToggle = (key) => (
    <button
      onClick={() => setExpanded(p => ({ ...p, [key]: !p[key] }))}
      style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 12, padding: 0 }}
    >
      {expanded[key] ? '[-]' : '[+]'}
    </button>
  );

  if (!data) return null;

  return (
    <div data-testid="grid-bot-dashboard" style={{ background: '#1f2937', borderRadius: 8, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 data-testid="grid-bot-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#e5e7eb' }}>
            Grid Bot
          </h3>
          <span data-testid="grid-bot-state" style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
            background: stateColor + '22', color: stateColor, textTransform: 'uppercase',
          }}>
            {displayState === 'grid_exhausted' ? 'EXHAUSTED (holding)' :
             displayState === 'exiting' && stats?.cb_flatten_active ? 'CB FLATTEN' :
             displayState === 'exiting' ? 'EXITING (limit orders)' : displayState}
          </span>
        </div>
        {(running || lastUpdated) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {running && stats?.running && (
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                {stats.runtime_hours?.toFixed(1)}h uptime
              </span>
            )}
            {running && stats?.running && (
              <span data-testid="grid-bot-data-source" style={{ fontSize: 11, color: '#6b7280' }}>
                source: {
                  data?.data_source === 'local_engine' ? 'live' :
                  data?.data_source === 'db_state_replica' ? 'live (replica)' :
                  data?.data_source === 'db_state_fallback' ? 'replica (warming)' :
                  'cache'
                }
              </span>
            )}
            {/* Last-updated + manual refresh (Feb 2026)
                When 502s hit or the tab was backgrounded, polling can
                stall silently. This makes the freshness visible and
                gives users a one-click recovery. */}
            <span
              data-testid="grid-bot-freshness"
              style={{
                fontSize: 11,
                color: fetchError
                  ? '#f87171'
                  : (lastUpdated && Date.now() - lastUpdated > 15000)
                    ? '#f59e0b'
                    : '#6b7280',
              }}
              title={lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : ''}
            >
              {fetchError
                ? '⚠ reconnecting…'
                : lastUpdated
                  ? `updated ${Math.max(0, Math.round((Date.now() - lastUpdated) / 1000))}s ago`
                  : 'loading…'}
            </span>
            <button
              data-testid="grid-bot-refresh-btn"
              onClick={fetchDashboard}
              style={{
                fontSize: 11,
                padding: '3px 10px',
                background: '#374151',
                color: '#e5e7eb',
                border: '1px solid #4b5563',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              ↻ Refresh
            </button>
          </div>
        )}
      </div>

      {running && data?.exchange_position_query_ok === false && (
        <div data-testid="exchange-position-query-warning" style={{ marginBottom: 10, fontSize: 12, color: '#f59e0b' }}>
          Exchange position sync delayed — hiding stale position until fresh query succeeds.
        </div>
      )}

      {running && data?.non_ranging_entry_pause_active && (
        <div data-testid="non-ranging-regime-warning" style={{ marginBottom: 10, fontSize: 12, color: '#f59e0b' }}>
          Strong trend detected: new entries paused.
          {data?.strong_trend_guard?.seconds_in_strong_trend > 0 && (
            <span>
              {' '}Strong trend active for {data.strong_trend_guard.seconds_in_strong_trend}s.
            </span>
          )}
        </div>
      )}

      {running && data?.hydra?.alerts?.foreign_standoff && (
        <ForeignStandoffBanner
          alerts={data.hydra.alerts}
          onResolve={async (action) => {
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${API}/api/grid-bot/foreign-standoff/resolve`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action, ack_reason: '' }),
              });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                alert(`Failed: ${err.detail || res.status}`);
              }
            } catch (e) {
              alert(`Request failed: ${e.message}`);
            }
          }}
        />
      )}

      {running && !data?.hydra?.alerts?.foreign_standoff && data?.hydra?.alerts?.margin_starved && (
        <div
          data-testid="margin-starved-banner"
          style={{
            marginBottom: 10, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(251,146,60,0.12)',
            border: '1px solid rgba(251,146,60,0.55)',
            fontSize: 12.5, color: '#fdba74', lineHeight: 1.5,
          }}
        >
          <strong style={{ color: '#fb923c' }}>⚠ Can't place orders — no free margin.</strong>{' '}
          <span style={{ color: '#fed7aa' }}>{data.hydra.alerts.margin_starved}</span>
        </div>
      )}

      {running && !data?.hydra?.alerts?.foreign_standoff && data?.hydra?.alerts?.zero_fill && (
        <div
          data-testid="zero-fill-banner"
          style={{
            marginBottom: 10, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(251,191,36,0.10)',
            border: '1px solid rgba(251,191,36,0.45)',
            fontSize: 12.5, color: '#fcd34d', lineHeight: 1.5,
          }}
        >
          <strong style={{ color: '#f59e0b' }}>⚠ Quoting but not filling —</strong>{' '}
          <span style={{ color: '#fde68a' }}>{data.hydra.alerts.zero_fill}</span>
        </div>
      )}

      {running && data?.hydra?.rebalance?.last_placement_error && (
        <div
          data-testid="placement-error-banner"
          style={{
            marginBottom: 10, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(248,113,113,0.10)',
            border: '1px solid rgba(248,113,113,0.45)',
            fontSize: 12.5, color: '#fca5a5', lineHeight: 1.5,
          }}
        >
          <strong style={{ color: '#f87171' }}>⚠ Order rejected by exchange —</strong>{' '}
          Hyperliquid rejected the last grid order and no resting order was placed.
          Fix the underlying cause (usually insufficient margin, minimum notional,
          or builder-fee approval) and the bot will retry automatically.
          <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 11.5, color: '#fda4a4', wordBreak: 'break-word' }}>
            {data.hydra.rebalance.last_placement_error}
          </div>
          {data.hydra.rebalance.placement_fail_count > 1 && (
            <div style={{ marginTop: 4, color: '#f87171', fontSize: 11 }}>
              {data.hydra.rebalance.placement_fail_count} consecutive failures since last successful placement.
            </div>
          )}
        </div>
      )}

      {running && !data?.hydra?.rebalance?.last_placement_error && data?.hydra?.rebalance?.last_diag?.empty_reason && (
        <div
          data-testid="empty-grid-diag-banner"
          style={{
            marginBottom: 10, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(251,191,36,0.10)',
            border: '1px solid rgba(251,191,36,0.45)',
            fontSize: 12.5, color: '#fcd34d', lineHeight: 1.5,
          }}
        >
          <strong style={{ color: '#f59e0b' }}>⚠ Rebalance built an empty grid —</strong>{' '}
          The last rebalance produced zero rungs, so nothing was sent to the
          exchange. That means the code path never reached the order-placement
          call. Root cause detected:
          <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 11.5, color: '#fde68a', wordBreak: 'break-word' }}>
            {data.hydra.rebalance.last_diag.empty_reason}
          </div>
          <div style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 11, color: '#fbbf24' }}>
            price=${(data.hydra.rebalance.last_diag.price || 0).toLocaleString()} anchor=${(data.hydra.rebalance.last_diag.anchor_price || 0).toLocaleString()} regime={data.hydra.rebalance.last_diag.regime} pause_buys={String(data.hydra.rebalance.last_diag.pause_buys)} pause_sells={String(data.hydra.rebalance.last_diag.pause_sells)} long={data.hydra.rebalance.last_diag.long_count} short={data.hydra.rebalance.last_diag.short_count} funding={JSON.stringify(data.hydra.rebalance.last_diag.funding_bias)} base_size={data.hydra.rebalance.last_diag.base_size}
          </div>
        </div>
      )}

      {running && data?.heartbeat_stale && (
        <div
          data-testid="stale-heartbeat-banner"
          style={{
            background: 'rgba(239,68,68,0.10)',
            border: '1px solid rgba(239,68,68,0.45)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: '#fecaca',
          }}
        >
          <strong style={{ color: '#f87171' }}>⛔ NO TICK FOR {Math.round((data.heartbeat_age_s || 0) / 60)} MIN —</strong>{' '}
          the running flag is still set but the engine has not completed a tick
          since{' '}
          {new Date((Date.now() / 1000 - (data.heartbeat_age_s || 0)) * 1000)
            .toISOString().replace('T', ' ').slice(0, 19)} UTC. Treat every
          number on this panel as stale: it is the last state the engine
          persisted, not live truth. Resting orders and any protective stop are
          still on the exchange and will behave exactly as placed — nothing is
          trailing or re-arming while this is showing.
        </div>
      )}


      {!running && data?.orphan_position?.open && (() => {
        const op = data.orphan_position;
        const long = (op.size || 0) > 0;
        const prot = op.protective;
        return (
        <div
          data-testid="orphan-position-banner"
          style={{
            marginBottom: 10, padding: '12px 14px', borderRadius: 8,
            background: op.covered ? 'rgba(251,191,36,0.12)' : 'rgba(239,68,68,0.14)',
            border: op.covered ? '2px solid rgba(251,191,36,0.6)' : '2px solid rgba(239,68,68,0.75)',
            fontSize: 13, color: op.covered ? '#fcd34d' : '#fca5a5', lineHeight: 1.55,
          }}
        >
          <strong style={{ color: op.covered ? '#f59e0b' : '#ef4444', fontSize: 14 }}>
            {op.covered
              ? '⚠ POSITION OPEN WITH NO ENGINE RUNNING'
              : '⛔ POSITION OPEN, NO ENGINE RUNNING, NO PROTECTIVE ORDER'}
          </strong>
          <div style={{ marginTop: 6 }} data-testid="orphan-position-detail">
            {Math.abs(op.size).toLocaleString()} {op.coin} {long ? 'LONG' : 'SHORT'}
            {op.entry ? <> from ${op.entry}</> : null}
            {typeof op.unrealized === 'number'
              ? <> · unrealised {op.unrealized >= 0 ? '+' : ''}${op.unrealized.toFixed(2)}</>
              : null}
          </div>
          {op.covered ? (
            <div style={{ marginTop: 6 }} data-testid="orphan-position-protection">
              Protected by a resting stop at <strong>${prot?.price}</strong> for{' '}
              {prot?.size?.toLocaleString()} {op.coin} (order {prot?.oid}).
              <div style={{ marginTop: 4, color: '#fbbf24' }}>
                That stop is <strong>static</strong>: it does not trail, re-arm or
                tighten while the engine is off. It is a floor on the worst case —
                the position is <strong>not being managed</strong>.
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 6 }} data-testid="orphan-position-protection">
              No resting reduce-only order covers the full position size
              {prot ? <> (largest is {prot.size?.toLocaleString()} @ ${prot.price})</> : null}.
              This position is <strong>unprotected</strong> — close it manually or
              start the engine so it can take control.
            </div>
          )}
        </div>
        );
      })()}

      {running && (data?.hydra?.regime?.pause_buys || data?.hydra?.regime?.pause_sells) && (() => {
        const pb = data?.hydra?.regime?.pause_buys;
        const ps = data?.hydra?.regime?.pause_sells;
        const bothAside = pb && ps;
        const drift = data?.hydra?.regime?.drift_pct;
        const driftTxt = typeof drift === 'number' ? ` (${drift.toFixed(1)}% / 12h)` : '';
        const regimeName = data?.hydra?.regime?.regime;
        const isStrong = regimeName === 'strong_trend';
        // One-sided guard: the counter-trend side is paused, but the bot is
        // STILL working a trend-aligned grid on the other side.
        const activeSide = pb ? 'sell' : 'buy';
        return (
        <div
          data-testid="drift-guard-banner"
          style={{
            marginBottom: 10, padding: '10px 12px', borderRadius: 8,
            background: bothAside ? 'rgba(248,113,113,0.08)' : 'rgba(56,189,248,0.08)',
            border: bothAside ? '1px solid rgba(248,113,113,0.4)' : '1px solid rgba(56,189,248,0.35)',
            fontSize: 12.5, color: bothAside ? '#fca5a5' : '#7dd3fc', lineHeight: 1.5,
          }}
        >
          {bothAside ? (
            <>
              <strong style={{ color: '#f87171' }}>🛑 Standing fully aside — 0 open orders by design.</strong>{' '}
              The 12h trend is {drift < 0 ? 'down' : 'up'} hard{driftTxt}, deep into a
              <strong> strong sustained move</strong>. A grid only bleeds catching a one-way
              trend, so the bot has cancelled <strong>both</strong> sides until it cools off.
              This is <em>deliberate capital protection, not a freeze</em> — orders return
              automatically when the trend fades.
            </>
          ) : (
            <>
              <strong style={{ color: '#38bdf8' }}>🛡️ Trend guard — trading WITH the trend.</strong>{' '}
              {isStrong ? <>Market is in a <strong>strong {drift < 0 ? 'down' : 'up'}trend</strong>{driftTxt}, so </>
                        : <>The 12h trend is {drift < 0 ? 'down' : 'up'}{driftTxt}, so </>}
              the bot paused <strong>new {pb ? 'longs' : 'shorts'}</strong> (counter-trend protection)
              {(() => {
                // Never assert resting orders — say what is actually resting
                // (09 Sep 2026: the banner claimed orders on the exchange
                // while Hyperliquid showed none for 30h).
                const resting = (data?.buy_levels_active || 0) + (data?.sell_levels_active || 0);
                if (!running) {
                  return <> and the engine is <strong>not running</strong>, so nothing is resting on the exchange.</>;
                }
                if (resting > 0) {
                  return <> and is <strong>still placing {activeSide}-side maker orders</strong> — {resting} order{resting === 1 ? '' : 's'} resting on the exchange now.</>;
                }
                return <> and currently has <strong>no orders resting</strong> on the exchange — the {activeSide} side is between placements.</>;
              })()}
            </>
          )}
        </div>
        );
      })()}

      {(() => {
        // TRUTH CHECK PANEL (10 Sep 2026). The old panel reported the engine's
        // beliefs as facts: 0 trips while four round trips sat in the database,
        // Total Fills as `undefined`, resting orders that were not there. This
        // states what has actually been compared against the exchange, and
        // labels anything unverified as unverified.
        const v = data?.verification;
        const rec = data?.reconcile;
        const divs = rec?.divergences || [];
        if (!v) return null;
        const bad = divs.length > 0;
        return (
          <div
            data-testid="truth-check-panel"
            style={{
              marginBottom: 12, padding: '10px 12px', borderRadius: 8,
              background: bad ? 'rgba(239,68,68,0.14)' : (v.stale ? 'rgba(148,163,184,0.10)' : 'rgba(34,197,94,0.08)'),
              border: `1px solid ${bad ? 'rgba(239,68,68,0.7)' : (v.stale ? 'rgba(148,163,184,0.4)' : 'rgba(34,197,94,0.4)')}`,
              fontSize: 12, color: bad ? '#fca5a5' : (v.stale ? '#cbd5e1' : '#86efac'),
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13 }} data-testid="truth-check-headline">
              {bad
                ? '⛔ ENGINE AND EXCHANGE DISAGREE'
                : (v.stale ? 'UNVERIFIED — no fresh truth check' : '✓ Verified against the exchange')}
            </div>
            {v.age_s !== null && v.age_s !== undefined && (
              <div style={{ marginTop: 3, color: '#94a3b8' }}>
                last checked {v.age_s}s ago
                {v.verified_fields?.length ? <> · verified: {v.verified_fields.join(', ')}</> : null}
              </div>
            )}
            {v.errors?.length > 0 && (
              <div style={{ marginTop: 4, color: '#fbbf24' }} data-testid="verification-errors">
                CHECK FAILED — {v.errors.join('; ')}. Fields covered by the
                failed check are NOT verified.
              </div>
            )}
            {v.unverified_fields?.length > 0 && (              <div style={{ marginTop: 3, color: '#94a3b8' }} data-testid="truth-check-unverified">
                UNVERIFIED: {v.unverified_fields.join(', ')} — these are the
                engine's own numbers, not confirmed against Hyperliquid
              </div>
            )}
            {divs.map((d, i) => (
              <div key={i} style={{ marginTop: 4 }} data-testid={`truth-check-divergence-${i}`}>
                <b>{d.field}</b> ({d.kind}): engine believed{' '}
                <code>{JSON.stringify(d.belief)}</code>, exchange reported{' '}
                <code>{JSON.stringify(d.reality)}</code>
                {d.delta !== undefined && d.delta !== null ? <> · delta {String(d.delta)}</> : null}
              </div>
            ))}
            {data?.stats_epoch_ts ? (
              <div style={{ marginTop: 4, color: '#94a3b8' }} data-testid="stats-epoch-note">
                Run totals count trades from{' '}
                {new Date(data.stats_epoch_ts * 1000).toISOString().replace('T', ' ').slice(0, 19)} UTC
                onwards. Exchange lifetime figures are all-time and cannot be reset.
              </div>
            ) : null}
          </div>
        );
      })()}

      {running && exchangePosition && (
        <div data-testid="exchange-position-info" style={{
          marginBottom: 14, background: '#1e293b', borderRadius: 6, padding: '8px 12px',
          borderLeft: `3px solid ${exchangePosition.size > 0 ? '#22c55e' : exchangePosition.size < 0 ? '#ef4444' : '#6b7280'}`,
        }}>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Exchange Position (Hyperliquid)</div>
          <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
            <span style={{ color: exchangePosition.size > 0 ? '#4ade80' : exchangePosition.size < 0 ? '#f87171' : '#9ca3af', fontWeight: 600 }}>
              {exchangePosition.size > 0 ? 'LONG' : exchangePosition.size < 0 ? 'SHORT' : 'FLAT'} {Math.abs(exchangePosition.size).toFixed(5)} {coin}
            </span>
            <span style={{ color: '#9ca3af' }}>Entry ${formatCoinPrice(exchangePosition.entry_price, coin)}</span>
            <span style={{ color: pnlColor(exchangePosition.unrealized_pnl) }}>
              {formatPnl(exchangePosition.unrealized_pnl)} unrealized
            </span>
          </div>
        </div>
      )}

      {running && state === 'exiting' && stats?.exit_protocol && (
        <div data-testid="exit-protocol-info" style={{
          marginBottom: 14, background: '#1e1a2e', borderRadius: 6, padding: '8px 12px',
          borderLeft: `3px solid ${stats?.cb_flatten_active ? '#ef4444' : '#f97316'}`,
        }}>
          <div style={{ fontSize: 11, color: stats?.cb_flatten_active ? '#ef4444' : '#f97316', marginBottom: 4, fontWeight: 600 }}>
            {stats?.cb_flatten_active
              ? (stats?.cb_flatten_trailing
                ? 'CB FLATTEN — Trailing Exit (minimising loss)'
                : 'CB FLATTEN — Closing Position')
              : 'EXIT PROTOCOL — Closing via Limit Orders'}
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap' }}>
            <span style={{ color: '#e2e8f0' }}>Reason: <b>{stats.exit_protocol.reason}</b></span>
            <span style={{ color: '#e2e8f0' }}>Attempt: {stats.exit_protocol.attempt}/{stats.exit_protocol.max_attempts}</span>
            <span style={{ color: '#e2e8f0' }}>Elapsed: {Math.round(stats.exit_protocol.elapsed_seconds)}s</span>
            {stats.exit_protocol.trailing_best_price > 0 && (
              <span style={{ color: '#60a5fa' }}>Trail Best: ${formatCoinPrice(stats.exit_protocol.trailing_best_price, coin)}</span>
            )}
            {stats.exit_protocol.long_remaining > 0 && (
              <span style={{ color: '#4ade80' }}>Long: {stats.exit_protocol.long_remaining.toFixed(5)}</span>
            )}
            {stats.exit_protocol.short_remaining > 0 && (
              <span style={{ color: '#f87171' }}>Short: {stats.exit_protocol.short_remaining.toFixed(5)}</span>
            )}
          </div>
        </div>
      )}

      {(running ? stats?.running : true) && (
        <div data-testid="grid-bot-stats" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#9ca3af' }}>Performance</span>
            {sectionToggle('stats')}
            <button
              type="button"
              data-testid="reset-session-stats-button"
              disabled={running}
              title={running ? 'Stop the bot to reset session stats' : 'Reset session stats'}
              onClick={() => { setResetError(null); setResetConfirmOpen(true); }}
              style={{
                marginLeft: 'auto',
                fontSize: 11,
                padding: '3px 9px',
                borderRadius: 4,
                border: '1px solid #374151',
                background: running ? '#1f2937' : '#0f172a',
                color: running ? '#4b5563' : '#9ca3af',
                cursor: running ? 'not-allowed' : 'pointer',
              }}
            >
              Reset
            </button>
          </div>
          {expanded.stats && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                {[
                  // `/exchange-stats` returns {} when the bot is not running,
                  // and {} is truthy in JS — that rendered "Total Fills
                  // undefined (undefinedB/undefinedS)" and an "All-Time P&L
                  // $0.00" that contradicted lifetime P&L (09 Sep 2026 P0).
                  ...(data?.exchange_stats && data.exchange_stats.total_fills !== undefined ? [
                    { label: 'Exchange Lifetime P&L', value: formatPnl(data.exchange_stats.net_pnl), color: pnlColor(data.exchange_stats.net_pnl) },
                    { label: 'Exchange Fees', value: `-${formatPnl(Math.abs(parseFloat(data.exchange_stats.total_fees) || 0)).replace(/^[+-]/, '')}`, color: '#ef4444' },
                    { label: 'Total Fills', value: `${data.exchange_stats.total_fills} (${data.exchange_stats.buy_fills ?? 0}B/${data.exchange_stats.sell_fills ?? 0}S)` },
                  ] : []),
                  { label: data?.stats_epoch_ts ? 'Run P&L' : 'Bot Lifetime P&L', value: formatPnl(stats?.net_pnl), color: pnlColor(stats?.net_pnl) },
                  { label: data?.stats_epoch_ts ? 'Run Round Trips' : 'Round Trips', value: stats?.total_round_trips || 0 },
                  { label: 'Win Rate', value: `${(stats?.win_rate || 0).toFixed(1)}%`, color: (stats?.win_rate || 0) >= 50 ? '#22c55e' : '#ef4444' },
                  { label: 'Max Drawdown', value: `${(stats?.max_drawdown_pct || 0).toFixed(2)}%`, color: '#ef4444' },
                  { label: 'Avg/Trip', value: formatPnl(stats?.avg_pnl_per_trip), color: pnlColor(stats?.avg_pnl_per_trip) },
                  // Fees are always a cost — never render them in profit green
                  // or with a leading "+".
                  { label: 'Lifetime Fees', value: `-${formatPnl(Math.abs(parseFloat(stats?.total_fees) || 0)).replace(/^[+-]/, '')}`, color: '#ef4444' },
                  { label: 'Rebalances', value: stats?.rebalance_count || 0 },
                  { label: 'Orders', value: `${stats?.orders_filled || 0}/${stats?.orders_placed || 0}` },
                ].map((s, i) => (
                <div key={`stat-${s.label}`} style={{ background: '#0f172a', borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: s.color || '#e5e7eb' }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {running && data?.regime_warmup?.warming_up && (
        <div
          data-testid="regime-warmup-banner"
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            background: 'rgba(245, 158, 11, 0.15)',
            border: '1px solid rgba(245, 158, 11, 0.4)',
            borderRadius: 6,
            fontSize: 12,
            color: '#fbbf24',
          }}
        >
          <strong>Warming up regime detection…</strong>{' '}
          ({data.regime_warmup.done}/{data.regime_warmup.required}). The
          bot is holding off placing any orders until ADX stabilises to
          avoid mis-placing into the wrong regime.

          {/* Market-data diag — shows the truth from connector each refresh.
              Critical when warmup is stuck: lets the user see if candles
              are actually arriving and from the right symbol. */}
          {data?.hydra?.market_data_diag && (
            <div
              data-testid="grid-bot-market-data-diag"
              style={{
                marginTop: 8,
                fontSize: 11,
                color: '#d1d5db',
                fontFamily: 'monospace',
              }}
            >
              Symbol: <strong>{data.hydra.market_data_diag.connector_symbol}</strong>
              {' · '}
              Candles: <strong>{data.hydra.market_data_diag.last_candle_count}</strong>
              {' · '}
              Last close: <strong>${Number(data.hydra.market_data_diag.last_candle_close).toFixed(4)}</strong>
              {' · '}
              Last refresh:{' '}
              <strong>
                {data.hydra.market_data_diag.last_refresh_ts
                  ? `${Math.round(Date.now() / 1000 - data.hydra.market_data_diag.last_refresh_ts)}s ago`
                  : 'never'}
              </strong>
              {data.hydra.market_data_diag.last_refresh_error && (
                <div style={{ color: '#ef4444', marginTop: 4 }}>
                  Error: {data.hydra.market_data_diag.last_refresh_error}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Idle reason banner — surfaces WHY the bot isn't placing orders.
          Range-break cool-down, width-guard, smart-stop cool-down, etc.
          The engine aggregates the reason into hydra.idle_reason. We
          suppress this when the warmup banner above is already showing
          (it's a sub-case of idle). */}
      {running && !data?.regime_warmup?.warming_up && data?.hydra?.idle_reason && (
        <div
          data-testid="grid-bot-idle-reason-banner"
          style={{
            marginBottom: 14,
            padding: 10,
            background: 'rgba(59, 130, 246, 0.12)',
            border: '1px solid rgba(59, 130, 246, 0.4)',
            borderRadius: 6,
            fontSize: 12,
            color: '#93c5fd',
          }}
        >
          <strong>Bot idle —</strong> {data.hydra.idle_reason}
        </div>
      )}

      {running && data?.hydra && (
        <HydraStatus hydra={data.hydra} />
      )}

      <div data-testid="grid-bot-grid-section" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#9ca3af' }}>Grid Levels</span>
          {sectionToggle('grid')}
          {grid?.center_price > 0 && (
            <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 'auto' }}>
              {grid.buy_levels?.length || 0} buys / {grid.sell_levels?.length || 0} sells
            </span>
          )}
        </div>
        {expanded.grid && renderGrid()}
        {expanded.grid && renderInventory()}
      </div>

      <ApiBudgetGauge />
      <BotActivityPanel />
      <DiagnosticsPanel />

      {/* Parameters section removed per user decision (2026-04-30):
          all engine parameters are baseline-locked; user controls live
          in the Control Panel (Position Size, Leverage, Regime pauses).
          The inline-edit UI here was unused after baseline lockdown. */}

      {/* Reset Session Stats — confirmation dialog */}
      {resetConfirmOpen && (
        <div
          data-testid="reset-stats-confirm-overlay"
          onClick={() => !resetting && setResetConfirmOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1f2937', borderRadius: 8, padding: 24, maxWidth: 460, width: '92%',
              border: '1px solid #374151', color: '#e5e7eb',
            }}
            data-testid="reset-stats-confirm-dialog"
          >
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
              Reset session stats?
            </h3>
            <p style={{ margin: 0, marginBottom: 8, fontSize: 13, color: '#d1d5db', lineHeight: 1.5 }}>
              This clears: Session P&amp;L, Round Trips, Win Rate, Max Drawdown,
              Avg/Trip, Session Fees, Rebalances, and Orders count.
            </p>
            <p style={{ margin: 0, marginBottom: 16, fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
              <strong style={{ color: '#fbbf24' }}>Note:</strong> All-Time P&amp;L, Closed P&amp;L,
              Exchange Fees, and Total Fills come from Hyperliquid and cannot be reset.
              Round-trip history (used by analytics) is preserved.
            </p>
            {resetError && (
              <div
                data-testid="reset-stats-error"
                style={{
                  background: '#7f1d1d', color: '#fecaca', padding: '8px 10px',
                  borderRadius: 4, fontSize: 12, marginBottom: 12,
                }}
              >
                {resetError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                data-testid="reset-stats-cancel-button"
                onClick={() => setResetConfirmOpen(false)}
                disabled={resetting}
                style={{
                  padding: '8px 14px', borderRadius: 4, border: '1px solid #4b5563',
                  background: '#374151', color: '#e5e7eb', cursor: resetting ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="reset-stats-confirm-button"
                onClick={handleResetStats}
                disabled={resetting}
                style={{
                  padding: '8px 14px', borderRadius: 4, border: 'none',
                  background: '#dc2626', color: '#fff', cursor: resetting ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600,
                }}
              >
                {resetting ? 'Resetting…' : 'Reset Stats'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GridBotDashboard;
